import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";

export const MONTHS = [
  "JAN", "FEB", "MAR", "APR", "MAY", "JUN",
  "JUL", "AUG", "SEP", "OCT", "NOV", "DEC",
];

export function fmtDate(d) {
  if (!d || !/^\d{4}-\d{2}-\d{2}$/.test(d)) {
    return { day: "--", mon: "", year: "", full: d || "" };
  }
  const [y, m, day] = d.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, day));
  return {
    day: String(day).padStart(2, "0"),
    mon: MONTHS[m - 1],
    year: String(y),
    full: dt.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      timeZone: "UTC",
    }),
  };
}

export const EVENT_STATUS_LABEL = {
  upcoming: "UPCOMING",
  sold_out: "SOLD OUT",
  cancelled: "CANCELLED",
  past: "PAST",
  out_now: "OUT NOW",
};

export const MEDIA_CATEGORIES = [
  { key: "all", label: "ALL" },
  { key: "live", label: "LIVE" },
  { key: "rave", label: "RAVE" },
  { key: "backstage", label: "BACKSTAGE" },
  { key: "press", label: "PRESS" },
  { key: "artist", label: "ARTIST" },
  { key: "video", label: "VIDEOS" },
  { key: "djset", label: "DJ SETS" },
];

export function useSite() {
  return useQuery({
    queryKey: ["site"],
    queryFn: async () => (await api.get("/public/site")).data,
    staleTime: 60_000,
  });
}

export function useUpcomingDates(limit) {
  return useQuery({
    queryKey: ["dates", "upcoming", limit || "all"],
    queryFn: async () =>
      (await api.get(`/public/dates?scope=upcoming${limit ? `&limit=${limit}` : ""}`)).data,
    staleTime: 60_000,
  });
}

export function usePastDates() {
  return useQuery({
    queryKey: ["dates", "past"],
    queryFn: async () => (await api.get("/public/dates?scope=past")).data,
    staleTime: 60_000,
  });
}

export function useReleases() {
  return useQuery({
    queryKey: ["releases"],
    queryFn: async () => (await api.get("/public/releases")).data,
    staleTime: 60_000,
  });
}

export function useMedia() {
  return useQuery({
    queryKey: ["media"],
    queryFn: async () => (await api.get("/public/media")).data,
    staleTime: 60_000,
  });
}

export function useReferences() {
  return useQuery({
    queryKey: ["references"],
    queryFn: async () => (await api.get("/public/references")).data,
    staleTime: 60_000,
  });
}

export function useSets() {
  return useQuery({
    queryKey: ["sets"],
    queryFn: async () => (await api.get("/public/sets")).data,
    staleTime: 30_000,
  });
}

export function usePageMeta(title, description, image) {
  useEffect(() => {
    if (title) document.title = title;
    if (description) {
      const m = document.querySelector('meta[name="description"]');
      if (m) m.setAttribute("content", description);
    }
    if (image) {
      const og = document.querySelector('meta[property="og:image"]');
      if (og) og.setAttribute("content", image);
      const tw = document.querySelector('meta[name="twitter:image"]');
      if (tw) tw.setAttribute("content", image);
    }
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", window.location.href);
  }, [title, description, image]);
}

export function useNoIndex() {
  useEffect(() => {
    document.title = "Admin — SEIMEN DEXTER";
    const m = document.querySelector('meta[name="robots"]');
    if (m) m.setAttribute("content", "noindex,nofollow");
  }, []);
}
